import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Checkbox,
    Divider,
    CircularProgress,
    Alert,
    LinearProgress,
    Autocomplete,
} from "@mui/material";
import {
    Close as CloseIcon,
    Add as AddIcon,
    CheckBox as CheckBoxIcon,
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    Public as PublicIcon,
    Lock as PrivateIcon,
    AttachFile as AttachFileIcon,
    PictureAsPdf as PdfIcon,
    Image as ImageIcon,
    OpenInNew as OpenInNewIcon,
    FolderSpecial as ProjectIcon,
    Description as QuoteIcon,
    DeleteSweep as RemoveAllIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import TaskService from "../../services/Task.service";
import ProjectService from "../../services/Project.service";
import UserService from "../../services/User.service";
import api from "../../services/api";

const EMPTY_FORM = {
    tasklist: "",
    is_public: true,
    assignee: null,
    project: null,
    quote: null,
    due_date: format(new Date(), "yyyy-MM-dd"),
    title: "",
    notes: "",
    attachmentFiles: [],
    existingAttachments: [],
    linkMode: "project",
    subtasks: [],
    newSubtaskText: "",
};

function fileIcon(file) {
    if (file?.type === "application/pdf")
        return <PdfIcon fontSize="small" color="error" />;
    return <ImageIcon fontSize="small" color="primary" />;
}

function fileIconByName(name) {
    if (name?.toLowerCase().endsWith(".pdf"))
        return <PdfIcon fontSize="small" color="error" />;
    return <ImageIcon fontSize="small" color="primary" />;
}

export default function AddTaskDialog({
    open,
    onClose,
    onSaved,
    currentUser,
    defaultProject,
    taskLists,
    item,
}) {
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const [userOptions, setUserOptions] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    const [projectOptions, setProjectOptions] = useState([]);
    const [projectInput, setProjectInput] = useState("");
    const [projectSearching, setProjectSearching] = useState(false);

    const [quoteOptions, setQuoteOptions] = useState([]);
    const [quoteInput, setQuoteInput] = useState("");
    const [quoteSearching, setQuoteSearching] = useState(false);

    const fileInputRef = useRef(null);

    const set = (field, val) => setForm((p) => ({ ...p, [field]: val }));

    useEffect(() => {
        if (!open) return;

        if (item) {
            const proj = item.project_detail || null;
            const quote = item.quote_detail || null;
            setForm({
                tasklist: item.tasklist || "",
                is_public: item.is_public ?? true,
                assignee: item.assignee_detail || null,
                project: proj,
                quote: quote,
                due_date: item.due_date || format(new Date(), "yyyy-MM-dd"),
                title: item.title || "",
                notes: item.notes || "",
                attachmentFiles: [],
                existingAttachments: item.attachments_detail || [],
                linkMode: quote ? "quote" : "project",
                subtasks: (item.subtasks_detail || []).map((st) => ({
                    id: st.id,
                    title: st.title,
                    is_complete: st.is_complete,
                })),
                newSubtaskText: "",
            });
            setProjectInput(
                proj ? [proj.number, proj.name].filter(Boolean).join(" - ") : ""
            );
            setProjectOptions(proj ? [proj] : []);
            setQuoteInput(
                quote ? [quote.number, quote.name].filter(Boolean).join(" - ") : ""
            );
            setQuoteOptions(quote ? [quote] : []);
        } else {
            setForm({
                ...EMPTY_FORM,
                due_date: format(new Date(), "yyyy-MM-dd"),
                assignee: currentUser || null,
                project: defaultProject || null,
                linkMode: "project",
            });
            setProjectInput(
                defaultProject
                    ? [defaultProject.number, defaultProject.name].filter(Boolean).join(" - ")
                    : ""
            );
            setProjectOptions(defaultProject ? [defaultProject] : []);
            setQuoteInput("");
            setQuoteOptions([]);
        }

        setError("");

        setUsersLoading(true);
        UserService.list()
            .then((res) => setUserOptions(Array.isArray(res.data) ? res.data : []))
            .catch(() => setUserOptions([]))
            .finally(() => setUsersLoading(false));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced project search
    useEffect(() => {
        if (form.linkMode !== "project") return;
        if (!projectInput.trim()) {
            setProjectOptions(form.project ? [form.project] : []);
            return;
        }
        const timer = setTimeout(() => {
            setProjectSearching(true);
            ProjectService.list({ search: projectInput, page_size: 20 })
                .then((res) => setProjectOptions(res.data.results || []))
                .catch(() => setProjectOptions([]))
                .finally(() => setProjectSearching(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [projectInput, form.linkMode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced quote search
    useEffect(() => {
        if (form.linkMode !== "quote") return;
        if (!quoteInput.trim()) {
            setQuoteOptions(form.quote ? [form.quote] : []);
            return;
        }
        const timer = setTimeout(() => {
            setQuoteSearching(true);
            api.get("/api/quotes/", { params: { search: quoteInput, page_size: 20 } })
                .then((res) => setQuoteOptions(res.data.results ?? res.data ?? []))
                .catch(() => setQuoteOptions([]))
                .finally(() => setQuoteSearching(false));
        }, 300);
        return () => clearTimeout(timer);
    }, [quoteInput, form.linkMode]); // eslint-disable-line react-hooks/exhaustive-deps

    const addSubtask = () => {
        if (!form.newSubtaskText.trim()) return;
        set("subtasks", [
            ...form.subtasks,
            { title: form.newSubtaskText.trim(), is_complete: false },
        ]);
        set("newSubtaskText", "");
    };

    const removeSubtask = (idx) => {
        set("subtasks", form.subtasks.filter((_, i) => i !== idx));
    };

    const toggleSubtask = (idx) => {
        set("subtasks", form.subtasks.map((s, i) =>
            i === idx ? { ...s, is_complete: !s.is_complete } : s
        ));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        set("attachmentFiles", [...form.attachmentFiles, ...files]);
        e.target.value = "";
    };

    const removeNewFile = (idx) => {
        set("attachmentFiles", form.attachmentFiles.filter((_, i) => i !== idx));
    };

    const removeExisting = (id) => {
        set("existingAttachments", form.existingAttachments.filter((a) => a.id !== id));
    };

    const handleSave = async () => {
        if (!form.title.trim() || !form.tasklist || !form.assignee) {
            setError("Title, Task List, and Assignee are required.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            const uploadedIds = [];
            for (const file of form.attachmentFiles) {
                const res = await TaskService.uploadAttachment(file);
                uploadedIds.push(res.data.id);
            }

            const existingIds = form.existingAttachments.map((a) => a.id);
            const allAttachmentIds = [...existingIds, ...uploadedIds];

            const payload = {
                tasklist: form.tasklist,
                assignee: form.assignee.id,
                project: form.linkMode === "project" && form.project ? form.project.id : null,
                quote: form.linkMode === "quote" && form.quote ? form.quote.id : null,
                due_date: form.due_date || null,
                title: form.title.trim(),
                notes: form.notes,
                is_public: form.is_public,
                attachment_ids: allAttachmentIds,
            };

            let taskId;
            let savedData;

            if (item) {
                const res = await TaskService.update(item.id, payload);
                taskId = item.id;
                savedData = res.data;

                // Subtask diffs
                const original = item.subtasks_detail || [];
                const currentIds = new Set(form.subtasks.filter((s) => s.id).map((s) => s.id));

                for (const orig of original) {
                    if (!currentIds.has(orig.id)) {
                        await TaskService.deleteSubTask(orig.id);
                    }
                }
                for (const st of form.subtasks.filter((s) => s.id)) {
                    const orig = original.find((o) => o.id === st.id);
                    if (orig && orig.is_complete !== st.is_complete) {
                        await TaskService.updateSubTask(st.id, { is_complete: st.is_complete });
                    }
                }
                for (const st of form.subtasks.filter((s) => !s.id)) {
                    await TaskService.createSubTask(taskId, { title: st.title });
                }
            } else {
                const res = await TaskService.create(payload);
                taskId = res.data.id;
                savedData = res.data;

                for (const st of form.subtasks) {
                    await TaskService.createSubTask(taskId, { title: st.title });
                }
            }

            onSaved(savedData);
            onClose();
        } catch (err) {
            setError("Failed to save task. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const userLabel = (u) =>
        u?.full_name || `${u?.first_name ?? ""} ${u?.last_name ?? ""}`.trim() || u?.username || "";

    const hasAttachments =
        form.attachmentFiles.length > 0 || form.existingAttachments.length > 0;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {item ? "Edit Task" : "New Task"}
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {saving && <LinearProgress sx={{ mb: 1 }} />}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {/* Task List + Visibility */}
                    <Grid item xs={12} sm={7}>
                        <FormControl fullWidth required>
                            <InputLabel>Select Task List</InputLabel>
                            <Select
                                label="Select Task List"
                                value={form.tasklist}
                                onChange={(e) => set("tasklist", e.target.value)}
                            >
                                {taskLists.map((tl) => (
                                    <MenuItem key={tl.id} value={tl.id}>
                                        {tl.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5} sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.is_public}
                                    onChange={(e) => set("is_public", e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    {form.is_public ? (
                                        <PublicIcon fontSize="small" color="primary" />
                                    ) : (
                                        <PrivateIcon fontSize="small" color="action" />
                                    )}
                                    <Typography variant="body2">
                                        {form.is_public ? "Public" : "Private"}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Grid>

                    {/* Assignee */}
                    <Grid item xs={12}>
                        <Autocomplete
                            options={userOptions}
                            value={form.assignee}
                            onChange={(_, val) => set("assignee", val)}
                            loading={usersLoading}
                            getOptionLabel={userLabel}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Assignee"
                                    required
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading && <CircularProgress size={16} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Grid>

                    {/* Project / Quote toggle */}
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() =>
                                    set("linkMode", form.linkMode === "project" ? "quote" : "project")
                                }
                                endIcon={
                                    form.linkMode === "project" ? (
                                        <ProjectIcon fontSize="small" />
                                    ) : (
                                        <QuoteIcon fontSize="small" />
                                    )
                                }
                                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                            >
                                {form.linkMode === "project" ? "PROJECTS" : "QUOTES"}
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                {form.linkMode === "project"
                                    ? "Link to a project — tap to switch to quotes"
                                    : "Link to a quote — tap to switch to projects"}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Project / Quote picker */}
                    <Grid item xs={12}>
                        {form.linkMode === "project" ? (
                            <Autocomplete
                                options={projectOptions}
                                value={form.project}
                                onChange={(_, val) => set("project", val)}
                                inputValue={projectInput}
                                onInputChange={(_, val) => setProjectInput(val)}
                                loading={projectSearching}
                                getOptionLabel={(p) =>
                                    p.number ? `${p.number} - ${p.name}` : p.name || ""
                                }
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Projects"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {projectSearching && <CircularProgress size={16} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        ) : (
                            <Autocomplete
                                options={quoteOptions}
                                value={form.quote}
                                onChange={(_, val) => set("quote", val)}
                                inputValue={quoteInput}
                                onInputChange={(_, val) => setQuoteInput(val)}
                                loading={quoteSearching}
                                getOptionLabel={(q) =>
                                    q.number ? `${q.number} - ${q.name}` : q.name || ""
                                }
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                filterOptions={(x) => x}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search Quotes"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {quoteSearching && <CircularProgress size={16} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        )}
                    </Grid>

                    {/* Due Date */}
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Due Date"
                            type="date"
                            fullWidth
                            value={form.due_date}
                            onChange={(e) => set("due_date", e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12}>
                        <TextField
                            label="Title"
                            fullWidth
                            required
                            value={form.title}
                            onChange={(e) => set("title", e.target.value)}
                            inputProps={{ maxLength: 100 }}
                            autoFocus={!item}
                        />
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12}>
                        <TextField
                            label={`Notes (${form.notes.length}/1000)`}
                            fullWidth
                            multiline
                            rows={5}
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            inputProps={{ maxLength: 1000 }}
                        />
                    </Grid>

                    {/* Subtasks */}
                    <Grid item xs={12}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Sub-tasks
                            {form.subtasks.length > 0 && (
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.disabled"
                                    sx={{ ml: 1 }}
                                >
                                    {form.subtasks.filter((s) => s.is_complete).length}/
                                    {form.subtasks.length} complete
                                </Typography>
                            )}
                        </Typography>
                        <List dense disablePadding>
                            {form.subtasks.map((st, idx) => (
                                <ListItem key={st.id ?? `new-${idx}`} disableGutters sx={{ py: 0.25 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        <Checkbox
                                            size="small"
                                            checked={st.is_complete}
                                            onChange={() => toggleSubtask(idx)}
                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                            checkedIcon={<CheckBoxIcon fontSize="small" color="success" />}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={st.title}
                                        primaryTypographyProps={{
                                            variant: "body2",
                                            sx: {
                                                textDecoration: st.is_complete ? "line-through" : "none",
                                                color: st.is_complete ? "text.disabled" : "text.primary",
                                            },
                                        }}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeSubtask(idx)}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                        <Box
                            sx={{ display: "flex", gap: 1, mt: 1 }}
                            component="form"
                            onSubmit={(e) => {
                                e.preventDefault();
                                addSubtask();
                            }}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Add a sub-task..."
                                value={form.newSubtaskText}
                                onChange={(e) => set("newSubtaskText", e.target.value)}
                                inputProps={{ maxLength: 100 }}
                                disabled={saving}
                            />
                            <IconButton
                                type="submit"
                                disabled={saving || !form.newSubtaskText.trim()}
                                color="primary"
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    </Grid>

                    {/* Attachment list */}
                    {hasAttachments && (
                        <Grid item xs={12}>
                            <List dense disablePadding>
                                {form.existingAttachments.map((att) => (
                                    <ListItem key={att.id} disableGutters>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            {fileIconByName(att.title)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={att.title}
                                            primaryTypographyProps={{ variant: "body2", noWrap: true }}
                                        />
                                        <ListItemSecondaryAction>
                                            {att.document_url && (
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={att.document_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeExisting(att.id)}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                                {form.attachmentFiles.map((f, idx) => (
                                    <ListItem key={`new-${idx}`} disableGutters>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                            {fileIcon(f)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={f.name}
                                            primaryTypographyProps={{ variant: "body2", noWrap: true }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => removeNewFile(idx)}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            {/* Attach file area */}
            <Box sx={{ px: 3, pt: 1 }}>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
                <Button
                    fullWidth
                    variant="contained"
                    color="info"
                    startIcon={<AttachFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={saving}
                >
                    ATTACH IMAGES / PDFs
                </Button>
                {hasAttachments && (
                    <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<RemoveAllIcon />}
                        onClick={() => {
                            set("attachmentFiles", []);
                            set("existingAttachments", []);
                        }}
                        disabled={saving}
                        sx={{ mt: 1 }}
                    >
                        REMOVE ALL ATTACHMENTS
                    </Button>
                )}
            </Box>

            <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={saving}>
                    CANCEL
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || !form.title.trim() || !form.tasklist || !form.assignee}
                    startIcon={saving ? <CircularProgress size={18} /> : null}
                >
                    SUBMIT
                </Button>
            </DialogActions>
        </Dialog>
    );
}
